import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../services/apiClient';
import BrandLogo from '../components/BrandLogo';
import WelcomeVideoOverlay from '../components/WelcomeVideoOverlay';
import NotificationBanner from '../components/NotificationBanner';
import TutorialVideoButton from '../components/TutorialVideoButton';
import { formatCurrency as formatCurrencyUtil, formatNumber, formatDate } from '../utils/currencyFormatter';
import {
  Package,
  Settings,
  LogOut,
  TrendingUp,
  DollarSign,
  AlertTriangle,
  ShoppingCart,
  RefreshCw,
  Truck,
  Receipt,
  Target,
  FileSpreadsheet,
  ChevronDown,
  Download,
  Wallet,
  BarChart3,
  Image as ImageIcon,
  Search,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const today = new Date().toISOString().split('T')[0];

  const [period, setPeriod] = useState(() => searchParams.get('period') || 'last_30_days');

  // Normalise date param to YYYY-MM-DD regardless of whether the URL uses DD-MM-YYYY
  const normalizeDateParam = (s) => {
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
      const [d, m, y] = s.split('-');
      return `${y}-${m}-${d}`;
    }
    return null;
  };

  // Custom date range state (only active when period === 'custom_range')
  const [customDateFrom, setCustomDateFrom] = useState(() => {
    return normalizeDateParam(searchParams.get('date_from'))
      || new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
  });
  const [customDateTo, setCustomDateTo] = useState(() => {
    return normalizeDateParam(searchParams.get('date_to')) || today;
  });
  const [meliMetrics, setMeliMetrics] = useState(null);
  const [expensesSummary, setExpensesSummary] = useState(null);
  const [taxEntries, setTaxEntries] = useState([]);

  const [loadingStats, setLoadingStats] = useState(false);
  const [syncMeta, setSyncMeta] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingOrders, setExportingOrders] = useState(false);
  const syncMetaLastFetch = useRef(0); // timestamp of last successful fetch

  // Redirect si no hay usuario
  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  // Track which period was last loaded so the [period] effect can skip on mount
  const dashboardLoadedRef = useRef(false);
  const loadedPeriodRef = useRef(null);

  // Cargar datos cuando hay usuario (primera vez)
  useEffect(() => {
    if (user && !dashboardLoadedRef.current) {
      dashboardLoadedRef.current = true;
      loadedPeriodRef.current = period;
      loadAllData(period, customDateFrom, customDateTo);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cuando cambia el periodo — recargar siempre
  // Para custom_range no auto-cargamos: el usuario debe confirmar las fechas con el botón
  useEffect(() => {
    if (!user || period === 'custom_range') return;
    if (loadedPeriodRef.current === period) return;
    loadedPeriodRef.current = period;
    loadAllData(period);
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build query string suffix for custom_range periods (for API calls)
  const buildCustomParams = (targetPeriod, dateFrom, dateTo) => {
    if (targetPeriod === 'custom_range') {
      return `&date_from=${dateFrom}&date_to=${dateTo}`;
    }
    return '';
  };

  // Build nav path carrying the current period (and date range if custom)
  const buildNavPath = (path) => {
    const params = new URLSearchParams({ period });
    if (period === 'custom_range' && customDateFrom && customDateTo) {
      params.set('date_from', customDateFrom);
      params.set('date_to', customDateTo);
    }
    return `${path}?${params.toString()}`;
  };

  // Cargar todos los datos para un periodo dado
  const loadAllData = async (targetPeriod, dateFrom = customDateFrom, dateTo = customDateTo) => {
    setLoadingStats(true);
    await Promise.all([
      fetchMeliMetrics(targetPeriod, dateFrom, dateTo),
      fetchExpensesSummary(targetPeriod, dateFrom, dateTo),
      fetchSyncMeta(),
      fetchTaxEntries(),
    ]);
    setLoadingStats(false);
  };

  // TTL: at most one request per 60 s regardless of how many times loadAllData fires
  const SYNC_META_TTL_MS = 60_000;

  const fetchSyncMeta = async () => {
    const now = Date.now();
    if (now - syncMetaLastFetch.current < SYNC_META_TTL_MS) return;
    syncMetaLastFetch.current = now;
    try {
      const r = await apiClient.get('/api/sync/metadata');
      setSyncMeta(r.data);
    } catch (e) {}
  };


  const fetchTaxEntries = async () => {
    try {
      const r = await apiClient.get('/api/tax-entries');
      setTaxEntries(r.data.taxes || []);
    } catch (e) { console.error(e); }
  };

  const fetchExpensesSummary = async (targetPeriod, dateFrom = customDateFrom, dateTo = customDateTo) => {
    const customParams = buildCustomParams(targetPeriod, dateFrom, dateTo);
    try {
      const r = await apiClient.get(`/api/expenses/summary?period=${targetPeriod}${customParams}`);
      setExpensesSummary(r.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMeliMetrics = useCallback(async (targetPeriod, dateFrom = customDateFrom, dateTo = customDateTo) => {
    const customParams = buildCustomParams(targetPeriod, dateFrom, dateTo);
    try {
      const response = await apiClient.get(`/api/dashboard/meli-metrics?period=${targetPeriod}${customParams}`);
      setMeliMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const handleApplyCustomRange = async () => {
    if (!customDateFrom || !customDateTo) {
      toast.error('Selecciona las fechas de inicio y fin');
      return;
    }
    if (customDateFrom > customDateTo) {
      toast.error('La fecha de inicio debe ser anterior a la fecha fin');
      return;
    }
    if (customDateTo > today) {
      toast.error('La fecha fin no puede ser posterior a hoy');
      return;
    }
    setLoadingStats(true);
    await loadAllData('custom_range', customDateFrom, customDateTo);
  };

  const handleExportOrders = async () => {
    setExportingOrders(true);
    try {
      const customParams = buildCustomParams(period, customDateFrom, customDateTo);
      const response = await apiClient.get(
        `/api/dashboard/export-orders?period=${period}${customParams}`,
        { responseType: 'blob' }
      );
      const periodLabel = period === 'custom_range'
        ? `${customDateFrom}_${customDateTo}`
        : period;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ordenes_${periodLabel}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV de órdenes descargado');
    } catch (error) {
      console.error('Export orders error:', error);
      toast.error('Error al descargar órdenes');
    } finally {
      setExportingOrders(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleExportDetailed = async () => {
    setExporting(true);
    try {
      const customParams = buildCustomParams(period, customDateFrom, customDateTo);
      const response = await apiClient.get(`/api/dashboard/export-detailed?period=${period}${customParams}`, {
        responseType: 'blob'
      });

      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename=([^;]+)/);
      const filename = match ? match[1].trim() : `analisis_detallado_${period}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Archivo Excel descargado');
    } catch (error) {
      let message = 'Error al exportar análisis';
      try {
        if (error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          message = json.detail || json.message || message;
        }
      } catch (_) {}
      console.error('Export error:', error);
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  // Obtener site del usuario para formato de moneda dinámico
  const userSite = user?.mercado_libre_site || 'MCO';
  
  const formatCurrency = (value) => {
    return formatNumber(value, userSite);
  };

  // OPTIMIZACIÓN CRÍTICA: Si tenemos datos en caché, mostrarlos INMEDIATAMENTE
  // aunque AuthContext esté verificando la sesión
  // Solo bloquear si loading=true Y no tenemos datos Y no tenemos usuario
  if (loading && !meliMetrics && !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-muted-foreground">Verificando sesión...</p>
        </div>
      </div>
    );
  }
  
  // Mostrar spinner solo si no tenemos métricas y estamos cargando
  if (loadingStats && !meliMetrics) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-muted-foreground">Cargando métricas...</p>
        </div>
      </div>
    );
  }

  const meli = meliMetrics?.meli_official || {};
  const costs = meliMetrics?.local_costs || {};
  const currency = meliMetrics?.currency || 'COP';

  // Usar comisión REAL si está disponible, sino usar estimada
  const commissionValue = costs.commission || costs.commission_estimated || 0;
  const isRealCommission = costs.commission_source === 'real';
  
  // Obtener valores separados
  const shippingFeeMeli = costs.shipping_fee_meli || 0;
  const retentionFee = costs.retention_fee || 0;
  const adsInvestment = meliMetrics?.ads_investment || 0;
  const cogsTotal = costs.cogs || 0;
  const operationalExpenses = expensesSummary?.total || 0;

  // Taxes: each entry is a % of ventas reales
  const salesAmount = meli.sales_amount || 0;
  const taxItems = taxEntries.map(t => ({
    ...t,
    amount: salesAmount * (t.percentage / 100),
  }));
  const taxesTotal = taxItems.reduce((sum, t) => sum + t.amount, 0);

  // Total Egresos ML + COGS + Ads + Gastos Operativos + Impuestos
  const totalEgresosMl = commissionValue + shippingFeeMeli + retentionFee;
  const totalEgresos = totalEgresosMl + cogsTotal + adsInvestment + operationalExpenses + taxesTotal;
  const netProfit = salesAmount - totalEgresos;
  const marginPct = salesAmount > 0 ? (netProfit / salesAmount * 100) : 0;
  
  // Alerta: gastos variables > 20% facturación
  const variableExpenses = expensesSummary?.variable_total || 0;
  const variableAlert = meli.sales_amount > 0 && variableExpenses > meli.sales_amount * 0.2;
  
  // Break-even: unidades promedio para cubrir gastos fijos
  const avgPrice = meli.units_sold > 0 ? (meli.sales_amount / meli.units_sold) : 0;
  const avgCost = meli.units_sold > 0 ? (totalEgresosMl + cogsTotal) / meli.units_sold : 0;
  const marginPerUnit = avgPrice - avgCost;
  const fixedExpenses = expensesSummary?.fixed_total || 0;
  const breakEvenUnits = marginPerUnit > 0 ? Math.ceil(fixedExpenses / marginPerUnit) : 0;

  return (
    <div className="min-h-screen bg-background">
      <WelcomeVideoOverlay />

      <nav className="bg-card/60 backdrop-blur-xl border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <BrandLogo className="h-6" />
            
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/cost')}
                data-testid="nav-costos"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Wallet className="w-5 h-5" />
                <span className="hidden md:inline">Costos</span>
              </button>

              <button
                onClick={() => navigate('/analytics')}
                data-testid="nav-analytics"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <BarChart3 className="w-5 h-5" />
                <span className="hidden md:inline">Analytics</span>
              </button>

              <button
                onClick={() => navigate('/profitability')}
                data-testid="nav-profitability"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <TrendingUp className="w-5 h-5" />
                <span className="hidden md:inline">Rentabilidad</span>
              </button>
              
              <button 
                onClick={() => navigate('/publications')}
                data-testid="nav-publications"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ImageIcon className="w-5 h-5" />
                <span className="hidden md:inline">Publicaciones</span>
              </button>
              
              <button
                onClick={() => navigate('/research')}
                data-testid="nav-research"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Target className="w-5 h-5" />
                <span className="hidden md:inline">Busq. Productos</span>
              </button>

              <div className="relative group">
                <button
                  disabled
                  data-testid="nav-inventory"
                  className="flex items-center gap-2 text-muted-foreground/70 cursor-not-allowed transition-colors"
                >
                  <Package className="w-5 h-5" />
                  <span className="hidden md:inline">Inventario</span>
                </button>
                <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-popover px-2 py-1 text-xs text-foreground opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                  Próximamente
                </div>
              </div>
              
              <button
                onClick={() => navigate('/referidos')}
                data-testid="nav-referrals"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Gift className="w-5 h-5" />
                <span className="hidden md:inline">Referidos</span>
              </button>

              <button 
                onClick={() => navigate('/settings')}
                data-testid="nav-settings"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Settings className="w-5 h-5" />
                <span className="hidden md:inline">Configuración</span>
              </button>
              
              <div className="border-l border-border pl-4 ml-2">
                <div className="flex items-center gap-3">
                  <div className="text-right hidden md:block">
                    <p className="text-sm font-medium text-foreground">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.mercado_libre_site}</p>
                  </div>
                  <button 
                    onClick={handleLogout}
                    data-testid="logout-button"
                    className="text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-6 py-8">
        <NotificationBanner />

        {/* Header con controles */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold font-outfit mb-2">Dashboard</h2>
            <p className="text-muted-foreground">Espejo del panel de MercadoLibre</p>
            {syncMeta?.minutes_ago != null && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <RefreshCw className="w-3 h-3 text-muted-foreground/70" />
                <span className="text-xs text-muted-foreground/70">
                  Última sincronización:{' '}
                  {syncMeta.minutes_ago < 60
                    ? `hace ${syncMeta.minutes_ago} min`
                    : syncMeta.minutes_ago < 1440
                      ? `hace ${Math.floor(syncMeta.minutes_ago / 60)}h ${syncMeta.minutes_ago % 60}min`
                      : `hace ${Math.floor(syncMeta.minutes_ago / 1440)} días`}
                  {syncMeta.is_stale && (
                    <span className="ml-1.5 text-amber-700 dark:text-amber-500">· datos desactualizados</span>
                  )}
                </span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <TutorialVideoButton video="Dashboard principal.mp4" caption="Recorrido por el Dashboard principal." />
            <button
              onClick={handleExportOrders}
              disabled={exportingOrders}
              data-testid="export-orders-button"
              className="bg-blue-600 hover:bg-blue-500 text-white font-medium py-2 px-4 rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {exportingOrders ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span className="text-sm">Descargando...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Descargar Órdenes
                </>
              )}
            </button>
            
            <button
              onClick={handleExportDetailed}
              disabled={exporting}
              data-testid="export-detailed-button"
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-medium py-2 px-4 rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {exporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span className="text-sm">Exportando...</span>
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4" />
                  Exportar Análisis
                </>
              )}
            </button>

            {/* Selector de periodo estilo MeLi */}
            <div className="relative">
              <div className="flex items-center gap-2">
                {loadingStats && (
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-border border-t-blue-400"></div>
                    <span className="text-xs">Actualizando...</span>
                  </div>
                )}
                <div className="bg-card/60 backdrop-blur-md border border-border rounded-lg px-4 py-2">
                  <select
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    disabled={loadingStats}
                    data-testid="period-selector"
                    className="bg-transparent text-foreground text-sm outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <option value="today" className="bg-popover">Hoy</option>
                    <option value="yesterday" className="bg-popover">Ayer</option>
                    <option value="last_7_days" className="bg-popover">Últimos 7 días</option>
                    <option value="last_30_days" className="bg-popover">Últimos 30 días</option>
                    <option value="this_month" className="bg-popover">Este mes</option>
                    <option value="custom_range" className="bg-popover">Rango personalizado</option>
                  </select>
                </div>
              </div>

              {period === 'custom_range' && (
                <div className="absolute top-full right-0 mt-2 z-50 flex items-center gap-2 bg-popover border border-border rounded-lg px-3 py-2 shadow-2xl">
                  <input
                    type="date"
                    value={customDateFrom}
                    max={customDateTo}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    disabled={loadingStats}
                    className="bg-transparent text-foreground text-sm outline-none cursor-pointer disabled:opacity-60"
                  />
                  <span className="text-muted-foreground/70 text-xs">—</span>
                  <input
                    type="date"
                    value={customDateTo}
                    min={customDateFrom}
                    max={today}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    disabled={loadingStats}
                    className="bg-transparent text-foreground text-sm outline-none cursor-pointer disabled:opacity-60"
                  />
                  <button
                    onClick={handleApplyCustomRange}
                    disabled={loadingStats}
                    className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-medium px-3 py-1 rounded transition-colors"
                  >
                    Aplicar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ===================== FILA 1: TOTALES OFICIALES DE MELI ===================== */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <h3 className="text-lg font-semibold text-yellow-600 dark:text-yellow-400">Números reales de tu negocio</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Ventas Reales */}
            <div className="bg-gradient-to-br from-yellow-600/10 to-yellow-800/10 border border-yellow-500/30 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <DollarSign className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                <span className="text-sm text-yellow-700 dark:text-yellow-300">Ventas Reales</span>
              </div>
              <p className="text-3xl font-bold text-foreground">${formatCurrency(meli.sales_amount)}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {meli.orders_count || 0} órdenes
                <span className="ml-2 text-green-600 dark:text-green-400">✓ monto real</span>
              </p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">Excluye devoluciones y cancelaciones</p>
            </div>

            {/* Unidades Vendidas */}
            <div className="bg-gradient-to-br from-blue-600/10 to-blue-800/10 border border-blue-500/30 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <ShoppingCart className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <span className="text-sm text-blue-700 dark:text-blue-300">Unidades Vendidas</span>
              </div>
              <p className="text-3xl font-bold text-foreground">{meli.units_sold || 0}</p>
              <p className="text-xs text-muted-foreground mt-2">productos</p>
            </div>

            {/* Comisiones Mercado Libre */}
            <div className="bg-gradient-to-br from-orange-600/10 to-orange-800/10 border border-orange-500/30 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Receipt className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                <span className="text-sm text-orange-700 dark:text-orange-300">Comisiones Mercado Libre</span>
              </div>
              <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">-${formatCurrency(costs.commission || 0)}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {isRealCommission ? (
                  <span className="text-green-600 dark:text-green-400">✓ valor real</span>
                ) : (
                  <span>estimado</span>
                )}
              </p>
            </div>

            {/* Costo de Envío */}
            <div className="bg-gradient-to-br from-cyan-600/10 to-cyan-800/10 border border-cyan-500/30 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Truck className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                <span className="text-sm text-cyan-700 dark:text-cyan-300">Costo de Envío</span>
              </div>
              <p className="text-3xl font-bold text-cyan-600 dark:text-cyan-400">-${formatCurrency(costs.shipping_fee_meli || 0)}</p>
              <p className="text-xs text-muted-foreground mt-2">envío asumido por vendedor</p>
            </div>

            {/* Publicidad */}
            <div className="bg-gradient-to-br from-cyan-600/10 to-cyan-800/10 border border-cyan-500/30 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Target className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                <span className="text-sm text-cyan-700 dark:text-cyan-300">Inversión Ads</span>
              </div>
              <p className="text-3xl font-bold text-cyan-600 dark:text-cyan-400">
                {meliMetrics?.ads_investment > 0 ? `-$${formatCurrency(meliMetrics?.ads_investment)}` : '$0'}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {meliMetrics?.ads_note || (meliMetrics?.ads_details?.length > 0 
                  ? `${meliMetrics.ads_details.length} campañas/items`
                  : 'Ver en Facturación')}
              </p>
            </div>
          </div>

          {/* Impuestos (Retenciones) - Módulo pequeño */}
          {costs.retention_fee > 0 && (
            <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                <span className="text-sm text-yellow-700 dark:text-yellow-300">Impuestos (Retenciones)</span>
              </div>
              <span className="text-lg font-bold text-yellow-600 dark:text-yellow-400">-${formatCurrency(costs.retention_fee)}</span>
            </div>
          )}
          
          {/* Nota sobre publicidad */}
          {meliMetrics?.ads_note && (
            <div className="mt-4 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
              <p className="text-xs text-cyan-700 dark:text-cyan-300">
                <strong>Nota:</strong> {meliMetrics.ads_note}
              </p>
            </div>
          )}
        </div>

        {/* ===================== FILA 2: COSTOS PROPIOS (COGS + Ads + Gastos) ===================== */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-rose-500 rounded-full"></div>
              <h3 className="text-lg font-semibold text-rose-600 dark:text-rose-400">Costos Operativos (Propios)</h3>
            </div>
            <button onClick={() => navigate('/cost')} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
              Gestionar Costos
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* COGS */}
            <div 
              className="bg-card/60 backdrop-blur-md border border-border rounded-2xl p-6 cursor-pointer hover:border-emerald-500/30 transition-colors group"
              onClick={() => navigate('/cost')}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Package className="w-6 h-6 text-rose-600 dark:text-rose-400" />
                  <span className="text-sm text-muted-foreground">Costo de Producto (COGS)</span>
                </div>
                <span className="text-xs text-emerald-600 dark:text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity">Editar →</span>
              </div>
              <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">-${formatCurrency(cogsTotal)}</p>
              <p className="text-xs text-muted-foreground/70 mt-2">
                {meli.sales_amount > 0 ? `${(cogsTotal / meli.sales_amount * 100).toFixed(1)}% de ventas` : ''}
              </p>
            </div>

            {/* Gastos Operativos */}
            <div className="bg-card/60 backdrop-blur-md border border-amber-500/30 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Wallet className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                <span className="text-sm text-amber-700 dark:text-amber-300">Gastos Operativos</span>
              </div>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {operationalExpenses > 0 ? `-$${formatCurrency(operationalExpenses)}` : '$0'}
              </p>
              <p className="text-xs text-muted-foreground/70 mt-2">
                {operationalExpenses > 0 && meli.sales_amount > 0 
                  ? `${(operationalExpenses / meli.sales_amount * 100).toFixed(1)}% de ventas` 
                  : 'Registra en Costos'}
              </p>
            </div>
          </div>

          {variableAlert && (
            <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
              <span className="text-sm text-red-700 dark:text-red-300">Alerta: Los gastos variables superan el 20% de las ventas reales</span>
            </div>
          )}
        </div>

        {/* ===================== IMPUESTOS ===================== */}
        {taxItems.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <h3 className="text-lg font-semibold text-blue-600 dark:text-blue-400">Impuestos</h3>
              </div>
              <button onClick={() => navigate('/cost')} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                Gestionar Impuestos
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {taxItems.map((t) => (
                <div key={t.tax_id} className="bg-card/60 backdrop-blur-md border border-blue-500/20 rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <Receipt className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm text-blue-700 dark:text-blue-300">{t.concept}</span>
                    <span className="text-xs text-muted-foreground/70 ml-auto">{t.percentage}%</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">-${formatCurrency(t.amount)}</p>
                  <p className="text-xs text-muted-foreground/70 mt-2">
                    {salesAmount > 0 ? `${t.percentage}% de ventas reales` : 'Sin ventas en el periodo'}
                  </p>
                </div>
              ))}
            </div>
            {taxItems.length > 1 && (
              <div className="mt-3 flex justify-end">
                <span className="text-sm text-blue-700 dark:text-blue-300 font-semibold">
                  Total impuestos: -${formatCurrency(taxesTotal)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* ===================== FILA 3: RENTABILIDAD NETA ===================== */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <h3 className="text-lg font-semibold text-green-600 dark:text-green-400">Rentabilidad Neta Real</h3>
          </div>
          
          <div className="bg-gradient-to-r from-green-600/20 via-emerald-600/20 to-teal-600/20 border border-green-500/30 rounded-2xl p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
              <div className="text-center md:text-left">
                <p className="text-sm text-muted-foreground mb-2">Ventas Reales</p>
                <p className="text-2xl font-bold text-foreground">${formatCurrency(meli.sales_amount)}</p>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">(-) Total Costos</p>
                <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">-${formatCurrency(totalEgresos)}</p>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-green-700 dark:text-green-300 mb-2 font-semibold">= UTILIDAD NETA</p>
                <p className={`text-4xl font-bold ${netProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  ${formatCurrency(netProfit)}
                </p>
              </div>

              <div className="text-center md:text-right space-y-2">
                <div>
                  <p className="text-xs text-muted-foreground">% Margen Neto</p>
                  <p className={`text-2xl font-bold ${marginPct >= 0 ? 'text-green-600 dark:text-green-400' : 'text-rose-600 dark:text-rose-400'}`}>
                    {marginPct.toFixed(1)}%
                  </p>
                </div>
                {breakEvenUnits > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground">Break-even</p>
                    <p className="text-lg font-semibold text-yellow-600 dark:text-yellow-400">{breakEvenUnits} uds</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ===================== DESGLOSE DETALLADO ===================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Desglose de Costos */}
          <div className="bg-card/60 backdrop-blur-md border border-border rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Desglose de Costos</h3>
            <div className="space-y-3">
              {[
                { label: 'Comisiones MercadoLibre', value: commissionValue, color: 'text-orange-600 dark:text-orange-400' },
                { label: 'Costo de Envio', value: shippingFeeMeli, color: 'text-cyan-600 dark:text-cyan-400' },
                { label: 'Retenciones', value: retentionFee, color: 'text-yellow-600 dark:text-yellow-400' },
                { label: 'Inversion en Publicidad', value: adsInvestment, color: 'text-cyan-600 dark:text-cyan-400' },
                { label: 'Costo de Producto (COGS)', value: cogsTotal, color: 'text-rose-600 dark:text-rose-400' },
                { label: 'Gastos Operativos', value: operationalExpenses, color: 'text-amber-600 dark:text-amber-400' },
                ...taxItems.map(t => ({ label: `${t.concept} (${t.percentage}%)`, value: t.amount, color: 'text-blue-600 dark:text-blue-400' })),
              ].filter(i => i.value > 0).map((item, idx) => {
                const pct = salesAmount > 0 ? (item.value / salesAmount * 100) : 0;
                return (
                  <div key={idx} className="flex justify-between items-center py-3 border-b border-border">
                    <span className="text-muted-foreground">{item.label}</span>
                    <div className="text-right">
                      <span className={`${item.color} font-semibold`}>${formatCurrency(item.value)}</span>
                      <span className="text-xs text-muted-foreground/70 ml-2">({pct.toFixed(1)}%)</span>
                    </div>
                  </div>
                );
              })}
              {/* Desglose de gastos por categoría */}
              {expensesSummary && Object.keys(expensesSummary.by_category || {}).length > 0 && (
                <div className="pl-4 border-l-2 border-amber-500/30 mt-2 space-y-2">
                  {Object.entries(expensesSummary.by_category).map(([cat, val]) => (
                    <div key={cat} className="flex justify-between items-center py-1">
                      <span className="text-xs text-muted-foreground/70">{cat}</span>
                      <span className="text-xs text-amber-600 dark:text-amber-400">${formatCurrency(val)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between items-center py-3 bg-muted rounded-lg px-3 mt-2">
                <span className="text-foreground font-semibold">TOTAL COSTOS</span>
                <span className="text-rose-700 dark:text-rose-300 font-bold text-lg">${formatCurrency(totalEgresos)}</span>
              </div>
            </div>
          </div>

          {/* Info del Periodo */}
          <div className="bg-card/60 backdrop-blur-md border border-border rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Información del Periodo</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-3 border-b border-border">
                <span className="text-muted-foreground">Periodo</span>
                <span className="text-foreground font-semibold">
                  {period === 'today' ? 'Hoy' :
                   period === 'yesterday' ? 'Ayer' :
                   period === 'last_7_days' ? 'Últimos 7 días' :
                   period === 'this_month' ? 'Este mes' : 'Últimos 30 días'}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-border">
                <span className="text-muted-foreground">Desde</span>
                <span className="text-muted-foreground text-sm">
                  {meliMetrics?.date_from ? new Date(meliMetrics.date_from).toLocaleDateString('es-CO') : '-'}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-border">
                <span className="text-muted-foreground">Hasta</span>
                <span className="text-muted-foreground text-sm">
                  {meliMetrics?.date_to ? new Date(meliMetrics.date_to).toLocaleDateString('es-CO') : '-'}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-border">
                <span className="text-muted-foreground">Moneda</span>
                <span className="text-foreground font-semibold">{currency}</span>
              </div>
            </div>
            
            {/* Links de debug */}
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground/70 mb-2">Herramientas de debug:</p>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate('/debug-shipping')}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                >
                  Análisis Envíos
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Nota informativa */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Nota:</strong> Los números de "Ventas Reales" provienen de las órdenes sincronizadas y excluyen 
            devoluciones y cancelaciones. Las comisiones son valores reales extraídos de MercadoPago.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
